import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import Image from '@/components/atoms/image/image';
import Typography from '@/components/atoms/typography/typography';
import {
  isImageGalleryBlockImageItem,
  isImageGalleryBlockYoutubeItem,
} from '@/components/blocks/image-gallery-block/image-gallery-block.adapter';
import ImageGalleryBlockControls from '@/components/blocks/image-gallery-block/image-gallery-block-controls/image-gallery-block-controls';
import MediaLightbox, {
  MediaSlide,
} from '@/components/molecules/media-lightbox/media-lightbox';
import Youtube from '@/components/molecules/youtube/youtube';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Row from '@/components/utilities/row/row';
import { getYoutubeId } from '@/libraries/getters/get-youtube-id';
import styles from './image-gallery-block.module.scss';

export interface ImageGalleryBlockProps {
  /** The class name to apply */
  className?: string;
  /** The items to show */
  items: Content.Components['ImageGalleryBlockDto']['items'];
}

const SWIPER_CONFIGURATION = {
  slidesPerGroup: 1,
  slidesPerView: 1,
  speed: 500,
};

const ImageGalleryBlock = ({ className, items }: ImageGalleryBlockProps) => {
  const [activeItemIndex, setActiveItemIndex] = useState<number>(0);
  const [isMediaLightboxOpen, setIsMediaLightboxOpen] = useState(false);

  const caption = items[activeItemIndex]?.captionText || '';

  const lightboxItems = useMemo<MediaSlide[]>(() => {
    return items.map(item => {
      if (isImageGalleryBlockImageItem(item)) {
        return {
          media: {
            $type: 'ImageAndVideoGridImageItemDto',
            captionText: item.captionText,
            id: item.id,
            image: item.image,
          } as Content.Properties.ImageAndVideoGridItem,
        };
      }

      return {
        media: {
          $type: 'ImageAndVideoGridYoutubeItemDto',
          captionText: item.captionText,
          id: item.id,
          video: item.video,
        } as Content.Properties.ImageAndVideoGridItem,
      };
    });
  }, [items]);

  const getImageItem = (
    item: Content.Properties.ImageGalleryBlockImageItem,
  ) => {
    const image = item.image;

    return (
      <div className={styles.imageWrapper}>
        <Image
          alt={image.altText}
          aspectRatio="sixteenByNine"
          className={styles.image}
          focalPoint={image.focalPoint}
          src={image.url}
          srcSet={{
            laptop: {
              width: 1114,
            },
          }}
          unoptimized
          width={668}
        />
      </div>
    );
  };

  const getVideoItem = (
    item: Content.Properties.ImageGalleryBlockYoutubeItem,
  ) => {
    const video = item.video;

    return (
      <Youtube
        className={styles.video}
        controls
        playButtonPosition="center"
        thumbnail={video.thumbnailImage}
        videoId={getYoutubeId(video.url)}
      />
    );
  };

  const handleExpand = () => {
    setIsMediaLightboxOpen(true);
  };

  return (
    <div className={classNames(styles.component, className)}>
      <Container>
        <Row className={styles.rowWrapper}>
          <Column className={styles.counter}>
            <Typography tagStyle="labelSmall">{`${activeItemIndex + 1} / ${items.length}`}</Typography>
          </Column>

          <Column>
            <Swiper
              key="swiper"
              {...SWIPER_CONFIGURATION}
              className={styles.swiper}
              loop={items.length > 1}
              onSlideChangeTransitionEnd={slide => {
                setActiveItemIndex(slide.realIndex);
              }}
              wrapperClass={styles.swiperWrapper}
            >
              {items.map((item, itemIndex) => {
                return (
                  <SwiperSlide key={itemIndex} className={styles.slide}>
                    {isImageGalleryBlockImageItem(item)
                      ? getImageItem(item)
                      : null}
                    {isImageGalleryBlockYoutubeItem(item)
                      ? getVideoItem(item)
                      : null}
                  </SwiperSlide>
                );
              })}

              <ImageGalleryBlockControls onClick={() => handleExpand()} />
            </Swiper>
          </Column>

          <Column>
            {caption && (
              <Typography className={styles.caption} tagStyle="labelMedium">
                {caption}
              </Typography>
            )}
          </Column>
        </Row>

        <MediaLightbox
          fullscreen
          isOpen={isMediaLightboxOpen}
          items={lightboxItems}
          onClose={() => setIsMediaLightboxOpen(false)}
          size="large"
          startIndex={activeItemIndex}
        />
      </Container>
    </div>
  );
};

ImageGalleryBlock.displayName = 'ImageGalleryBlock';

export default ImageGalleryBlock;
